import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Archive } from '../api/archives';
import { activeClipsAt, formatTime, totalDuration } from '../format/helpers';
import type { Edit } from '../format/types';
import { useKeyBindings } from '../keys/useKeyScope';
import { getClipCache } from '../engine/clipcache';
import { syncClipCacheStatus, useClipCacheStore } from '../state/clipcache';
import { usePlayback } from '../state/playback';
import { useTimeline } from '../state/timeline';
import type { PlayerControls } from './EditorPage';
import { MAX_PPS, MIN_PPS } from './timeline/constants';
import { TimelineProvider } from './timeline/context';
import ErrorBoundary from './ErrorBoundary';
import RemotePlayheads from './collab/RemotePlayheads';
import { Ruler, Playhead } from './timeline/Ruler';
import { TrackHeader } from './timeline/TrackHeader';
import { TrackLane } from './timeline/TrackLane';
import './Timeline.css';

interface Props {
  archives: Archive[];
  controls: PlayerControls;
}

// --- main component ----------------------------------------------------------

export default function Timeline({ archives, controls }: Props) {
  const edit = useTimeline((s) => s.edit);
  const selection = useTimeline((s) => s.selection);
  const [pps, setPps] = useState(20);
  const [viewport, setViewport] = useState<[number, number]>([0, 1000]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // track-header column — its vertical scroll is mirrored from the lane pane
  const labelsRef = useRef<HTMLDivElement | null>(null);
  // lane DOM nodes by track id — measured at clip-drag start for vertical moves
  const laneRefs = useRef(new Map<string, HTMLDivElement>());
  // lane a dragged clip would land on (same-kind, ≠ origin) — for highlighting
  const [dropTrackId, setDropTrackId] = useState<string | null>(null);

  const duration = totalDuration(edit);
  const contentWidth = Math.max((duration + 60) * pps, 1200);

  // Stable identity or every context consumer re-renders on every Timeline
  // render (context bypasses React.memo) — scroll alone would repaint every
  // clip on a long timeline.
  const ctxValue = useMemo(
    () => ({ pps, controls, laneRefs, dropTrackId, setDropTrack: setDropTrackId }),
    [pps, controls, dropTrackId],
  );

  // Clip culling window (px), quantized so memoized lanes only re-render on
  // chunk crossings, with a full viewport of overscan on each side — generous
  // enough that a clip dragged past the edge unmounting mid-drag stays rare
  // (harmless even then: drag listeners live on `window` with closure state).
  const CULL_CHUNK_PX = 500;
  const overscan = Math.max(viewport[1] - viewport[0], 600);
  const cullLeft = Math.floor(Math.max(0, viewport[0] - overscan) / CULL_CHUNK_PX) * CULL_CHUNK_PX;
  const cullRight = Math.ceil((viewport[1] + overscan) / CULL_CHUNK_PX) * CULL_CHUNK_PX;
  const cullRange = useMemo<[number, number]>(() => [cullLeft, cullRight], [cullLeft, cullRight]);

  const zoomAt = useCallback(
    (factor: number, anchorClientX?: number) => {
      setPps((old) => {
        const next = Math.min(MAX_PPS, Math.max(MIN_PPS, old * factor));
        const el = scrollRef.current;
        if (el && next !== old) {
          const rect = el.getBoundingClientRect();
          const ax = anchorClientX !== undefined ? anchorClientX - rect.left : el.clientWidth / 2;
          const t = (el.scrollLeft + ax) / old;
          requestAnimationFrame(() => {
            el.scrollLeft = t * next - ax;
          });
        }
        return next;
      });
    },
    [],
  );

  // ctrl/cmd + wheel zoom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  // +/- keyboard zoom
  useKeyBindings(
    () => [
      {
        id: 'timeline.zoomIn',
        keys: ['+', '='],
        scope: 'global',
        group: 'Timeline',
        description: 'Zoom in',
        run: () => zoomAt(1.25),
      },
      {
        id: 'timeline.zoomOut',
        keys: ['-', '_'],
        scope: 'global',
        group: 'Timeline',
        description: 'Zoom out',
        run: () => zoomAt(1 / 1.25),
      },
    ],
    [zoomAt],
  );

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    setViewport([el.scrollLeft, el.scrollLeft + el.clientWidth]);
    // keep the track-header column vertically aligned with the lanes
    if (labelsRef.current) labelsRef.current.scrollTop = el.scrollTop;
  }
  useEffect(onScroll, [pps, contentWidth]);

  // Cache status is derived from actual Cache API contents, so it must be
  // recomputed when the edit changes (a trim can move a clip out of its cached
  // range) as well as on first load.
  useEffect(() => {
    void getClipCache()
      .init()
      .then(() => syncClipCacheStatus(edit, archives));
  }, [edit, archives]);

  // Storage can be cleared in another tab or evicted while we are backgrounded.
  // Nothing in the normal flow would notice, so the bars would keep claiming
  // clips are cached — re-read the store whenever the page comes back.
  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      void getClipCache()
        .refresh()
        .then(() => syncClipCacheStatus(edit, archives));
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [edit, archives]);

  const tl = useTimeline.getState();

  // Detach only does something for an un-muted video clip; anything else in the
  // selection is skipped, so don't offer the button unless one is there.
  const detachable = useMemo(
    () =>
      edit.tracks.some(
        (t) =>
          t.kind === 'video' && t.clips.some((c) => selection.includes(c.id) && !c.muted),
      ),
    [edit.tracks, selection],
  );

  return (
    <div className="timeline">
      <div className="tl-toolbar">
        <button
          onClick={() => tl.addTextClip(usePlayback.getState().currentTime)}
          title="Add a text overlay at the playhead"
        >
          + Text
        </button>
        <button
          disabled={!selection.length}
          onClick={() =>
            tl.splitAt(usePlayback.getState().currentTime, useTimeline.getState().selection)
          }
          title="Split selected clip at playhead (S)"
        >
          Split
        </button>
        <button
          disabled={!detachable}
          onClick={() => tl.detachAudio(useTimeline.getState().selection)}
          title="Move the selected clips' audio onto its own track and mute the video (⇧S)"
        >
          Detach Audio
        </button>
        <button
          disabled={!selection.length}
          onClick={() => tl.deleteClips(useTimeline.getState().selection)}
          title="Delete selected (⌫)"
        >
          Delete
        </button>
        <div className="tl-toolbar-spacer" />
        <CacheStatus />
        <MergeNotice />
        <EditNotice />
        <DecoderWarning edit={edit} />
        <span className="timecode">{formatTime(duration, true)}</span>
        <button className="icon" onClick={() => zoomAt(1 / 1.25)} title="Zoom out (-)">
          −
        </button>
        <button className="icon" onClick={() => zoomAt(1.25)} title="Zoom in (+)">
          +
        </button>
      </div>

      <TimelineProvider value={ctxValue}>
        <div className="tl-body">
          <div className="tl-labels">
            <div className="tl-labels-ruler" />
            <div className="tl-labels-scroll" ref={labelsRef}>
              {edit.tracks.map((track, i) => (
                <TrackHeader key={track.id} track={track} index={i} tracks={edit.tracks} />
              ))}
            </div>
            <div className="tl-add-tracks">
              <button onClick={() => tl.addTrack('video')} title="Add video track">
                ＋V
              </button>
              <button onClick={() => tl.addTrack('overlay')} title="Add text/overlay track">
                ＋T
              </button>
              <button onClick={() => tl.addTrack('audio')} title="Add audio track">
                ＋A
              </button>
            </div>
          </div>

          <div className="tl-scroll" ref={scrollRef} onScroll={onScroll}>
            <div className="tl-content" style={{ width: contentWidth }}>
              <Ruler viewport={viewport} width={contentWidth} />
              {edit.tracks.map((track) => (
                <TrackLane key={track.id} track={track} archives={archives} cullRange={cullRange} />
              ))}
              <ErrorBoundary label="Presence">
                <RemotePlayheads />
              </ErrorBoundary>
              <Playhead />
            </div>
          </div>
        </div>
      </TimelineProvider>
    </div>
  );
}

/**
 * Caching progress. Deliberately says how many clips remain rather than a
 * single percentage — a clip either finishes and becomes durably cached, or it
 * doesn't. Alt+R cancels.
 */
function CacheStatus() {
  const pending = useClipCacheStore((s) => s.pending);
  const total = useClipCacheStore((s) => s.totalQueued);
  if (!pending) return null;
  const done = Math.max(0, total - pending);
  return (
    <span className="tl-cache-status" title="Caching clips locally — Alt+R to cancel">
      caching {done + 1} of {total || pending}
    </span>
  );
}

/**
 * Subtle decoder-pressure hint: many stacked video tracks (or many clips
 * active at the playhead) can outrun what the browser will decode at once.
 */
/**
 * Concurrent edits merged into an overlap and repair manufactured a crossfade
 * nobody authored (by design — see docs/realtime-collab.md). The transition is
 * already visible as a ⧓ badge; this makes sure it is *noticed*.
 */
function MergeNotice() {
  const notice = useTimeline((s) => s.mergeNotice);
  if (!notice) return null;
  return (
    <button
      className="tl-merge-notice"
      onClick={() => useTimeline.getState().dismissMergeNotice()}
      title="Concurrent edits overlapped, so the overlap became a crossfade. Click to dismiss; select the ⧓ badge on the track to review or remove it."
    >
      ⧓ {notice} ✕
    </button>
  );
}

/** An action did less than the selection implied — say so instead of no-oping. */
function EditNotice() {
  const notice = useTimeline((s) => s.editNotice);
  if (!notice) return null;
  return (
    <button
      className="tl-merge-notice"
      onClick={() => useTimeline.getState().dismissEditNotice()}
      title="Click to dismiss"
    >
      {notice} ✕
    </button>
  );
}

function DecoderWarning({ edit }: { edit: Edit }) {
  const t = usePlayback((s) => s.currentTime);
  const videoTracks = edit.tracks.filter((tr) => tr.kind === 'video');
  let active = 0;
  for (const track of videoTracks) active += activeClipsAt(track, t).length;
  if (videoTracks.length <= 3 && active <= 6) return null;
  return (
    <span
      className="tl-decoder-warning"
      title="Browsers only decode a handful of videos at once — consider fewer simultaneous video tracks"
    >
      ⚠ many stacked videos — preview may drop frames
    </span>
  );
}
